import { useEffect, useState } from "react";
import { BrainCircuit, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const labelCls = "text-xs text-[#8A93A6] tracking-wider";
const inputCls = "mt-1 w-full bg-[#101318] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 outline-none";

export default function AdminAI() {
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/ai/settings"), api.get("/admin/ai/usage")]);
      setSettings(s.data);
      setUsage(u.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/ai/settings", {
        enabled: settings.enabled,
        model: settings.model,
        plan_credits: settings.plan_credits,
        costs: settings.costs,
      });
      toast.success("PlusMaker AI settings saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy("");
  };

  if (!settings || !usage) return <p className="text-sm text-[#64748B]">Loading PlusMaker AI management…</p>;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="admin-ai-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>PlusMaker AI Management</h1>
        <p className="text-sm text-[#8A93A6] mt-2">Controls PlusMaker AI only — existing subscription plans, prices and billing are never affected.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[["Members Using AI (this cycle)", usage.total_members, "ai-stat-members"],
          ["AI Credits Consumed", usage.total_credits_used, "ai-stat-credits"],
          ["Successful Requests", usage.total_requests, "ai-stat-requests"]].map(([label, val, tid]) => (
          <div key={tid} data-testid={tid} className="rounded-2xl border border-[#D4AF37]/15 bg-[#101318] p-4">
            <div className="text-xs text-[#8A93A6] tracking-wider">{label}</div>
            <div className="text-2xl font-bold text-[#E9C766] mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#101318] p-6 space-y-6" data-testid="ai-settings-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrainCircuit size={18} className="text-[#D4AF37]" />
            <span className="text-sm font-bold tracking-[0.15em] text-[#E9C766]">AI CONFIGURATION</span>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#8A93A6]">
            <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="accent-[#D4AF37]" data-testid="ai-enabled-toggle" />
            PlusMaker AI Available
          </label>
        </div>

        <div>
          <label className={labelCls}>AI Engine Model (server-side only — never shown to members)</label>
          <input value={settings.model} onChange={(e) => setSettings({ ...settings, model: e.target.value })} className={inputCls} data-testid="ai-model-input" />
        </div>

        <div>
          <p className={labelCls}>Monthly AI Credits per Plan</p>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {["FREE", "PRO", "ADVANCE"].map((plan) => (
              <div key={plan}>
                <label className="text-[10px] font-mono tracking-widest text-[#64748B]">{plan}</label>
                <input type="number" min="0" value={settings.plan_credits[plan]} data-testid={`ai-credits-${plan.toLowerCase()}`}
                  onChange={(e) => setSettings({ ...settings, plan_credits: { ...settings.plan_credits, [plan]: Number(e.target.value) } })}
                  className={inputCls} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className={labelCls}>AI Credit Cost per Request Type</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            {[["question", "Basic Question"], ["market_analysis", "Market Analysis"], ["signal", "Signal Request"], ["chart_analysis", "Chart Analysis"]].map(([key, label]) => (
              <div key={key}>
                <label className="text-[10px] font-mono tracking-widest text-[#64748B]">{label}</label>
                <input type="number" min="1" value={settings.costs[key]} data-testid={`ai-cost-${key}`}
                  onChange={(e) => setSettings({ ...settings, costs: { ...settings.costs, [key]: Number(e.target.value) } })}
                  className={inputCls} />
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="ai-settings-save"
          className="px-6 py-2.5 rounded-xl text-sm font-bold tracking-[0.15em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-40 hover:brightness-110 transition flex items-center gap-2">
          <Save size={15} /> {busy ? "SAVING…" : "SAVE AI SETTINGS"}
        </button>
      </div>

      <div className="rounded-2xl border border-white/5 overflow-hidden overflow-x-auto" data-testid="ai-usage-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#101318] text-[#8A93A6] text-xs tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">MEMBER</th><th className="text-left px-4 py-3">PLAN</th>
              <th className="text-left px-4 py-3">CREDITS USED</th><th className="text-left px-4 py-3">LIMIT</th>
              <th className="text-left px-4 py-3">REQUESTS</th>
            </tr>
          </thead>
          <tbody>
            {usage.rows.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-[#64748B]">No AI usage in the current cycle yet.</td></tr>}
            {usage.rows.map((r) => (
              <tr key={r.member_uid || r.member_email} className="border-t border-white/5" data-testid={`ai-usage-row-${r.member_uid}`}>
                <td className="px-4 py-3"><div className="text-white">{r.member_name}</div><div className="text-xs text-[#64748B]">{r.member_uid} · {r.member_email}</div></td>
                <td className="px-4 py-3 text-[#E9C766] font-bold text-xs">{r.plan}</td>
                <td className="px-4 py-3 text-white font-semibold">{r.used}</td>
                <td className="px-4 py-3 text-[#8A93A6]">{r.limit}</td>
                <td className="px-4 py-3 text-[#8A93A6]">{r.requests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
